import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Mail } from "lucide-react"
import { useState } from "react"
import { usePlatformParams } from "@/hooks/use-platform-params"
import { resetPwSendEmail } from "@/api/auth"
import type { ResetPasswordSendEmailCredentials, ResetPasswordSendEmailResponse } from "@/types/auth"
import type { ApiResponse } from "@/types/api"
import { toast } from 'sonner'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { platformParams } = usePlatformParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('이메일 주소를 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const credentials: ResetPasswordSendEmailCredentials = {
        email
      };

      const platform = platformParams.platform || 'cafe24';
      const response: ApiResponse<ResetPasswordSendEmailResponse> = await resetPwSendEmail(credentials, platform);

      // API Response가 성공적이면 code가 200이거나 2xx 범위
      if (response.code && response.code >= 200 && response.code < 300) {
        setIsSuccess(true);
        toast.success('비밀번호 재설정 링크를 이메일로 전송했습니다.');
        
        // 성공 메시지 표시 후 3초 뒤 다이얼로그 닫기
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        toast.error(response.message || '이메일 전송에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 재설정 이메일 전송 오류:', error);
      toast.error('이메일 전송 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // 다이얼로그 닫을 때 상태 초기화
    setEmail("");
    setIsLoading(false);
    setIsSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isSuccess ? '이메일을 전송했습니다!' : '비밀번호를 잊으셨나요?'}
          </DialogTitle>
          <DialogDescription>
            {isSuccess ? (
              <>
                입력하신 이메일로 비밀번호 재설정 링크를 전송했습니다. <br />
                이메일을 확인해주세요. 잠시 후 자동으로 닫힙니다.
              </>
            ) : (
              <>
                가입된 이메일로 비밀번호 재설정 링크를 보내드려요. <br />
                비밀번호 재설정은 10분간 유효합니다.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">이메일 주소</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="이메일 주소를 입력하세요"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  취소
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={isLoading}
                >
                  {isLoading ? "전송 중..." : "이메일 전송하기"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm text-muted-foreground">
                {email}으로 이메일이 전송되었습니다.
              </p>
              <Button onClick={handleClose} className="w-full">
                확인
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}